import { useEffect, useState } from 'react';
import {
  BROADCAST_TARGETS,
  BROADCAST_TARGET_LABEL,
  canRecall,
  NOTIFY_CHANNELS,
  NOTIFY_CHANNEL_HINT,
  NOTIFY_CHANNEL_LABEL,
  PROFILE_TYPES,
  PROFILE_TYPE_LABEL,
  ROLES,
  ROLE_LABEL,
  VERIFY_STATES,
  VERIFY_STATE_LABEL,
  type BroadcastResult,
  type BroadcastRow,
  type BroadcastTarget,
  type NotifyChannel,
  type ProfileType,
  type Role,
  type VerifyState,
} from 'shared';
import { api } from '../api';
import AdminList from '../AdminList';
import AdminSheet from '../AdminSheet';
import { alert, confirm } from '../telegram';
import { useFormErrors } from '../useFormErrors';
import FieldError from '../FieldError';

/**
 * Рассылки.
 *
 * Экран построен вокруг двух вопросов: скольким это уйдёт и как это
 * потом убрать. Сначала прогон без отправки, потом подтверждение с числом
 * получателей — и только затем отправка.
 *
 * Отправленное можно убрать или поправить у всех сразу, но не всегда:
 * Telegram даёт трогать свои сообщения примерно двое суток. Поэтому
 * кнопки исчезают, когда срок вышел, — предлагать действие, которое
 * заведомо не сработает, хуже, чем не предлагать вовсе.
 */
export default function AdminBroadcast() {
  const [channels, setChannels] = useState<NotifyChannel[]>(['TELEGRAM']);
  /*
   * Куда шлём. Два разных действия под одной формой: письмо каждому
   * лично и одно сообщение в общий чат. Разница не в оформлении, а в
   * том, что во втором случае получатель один, и сегменты ни при чём.
   */
  const [target, setTarget] = useState<BroadcastTarget>('USERS');
  const [chatId, setChatId] = useState('');
  const [chatCheck, setChatCheck] = useState<{ ok: boolean; text: string } | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [role, setRole] = useState<Role | ''>('');
  const [profileType, setProfileType] = useState<ProfileType | ''>('');
  const [verifyState, setVerifyState] = useState<VerifyState | ''>('');
  const [activeDays, setActiveDays] = useState<number | ''>('');
  const [withCompany, setWithCompany] = useState<'' | 'yes' | 'no'>('');

  const [preview, setPreview] = useState<BroadcastResult | null>(null);
  const [history, setHistory] = useState<BroadcastRow[]>([]);
  const [busy, setBusy] = useState(false);
  /** Какую отправленную рассылку разбираем. */
  const [expanded, setExpanded] = useState<string | null>(null);
  const form = useFormErrors();

  const openSent = history.find((b) => b.id === expanded) ?? null;

  const audience = {
    role: role || undefined,
    profileType: profileType || undefined,
    verifyState: verifyState || undefined,
    activeDays: activeDays || undefined,
    withCompany: withCompany === '' ? undefined : withCompany === 'yes',
  };

  async function loadHistory() {
    try {
      setHistory((await api.admin.broadcasts()).items);
    } catch {
      // История необязательна — молчим, чтобы не мешать основной работе.
    }
  }

  useEffect(() => {
    void loadHistory();
  }, []);

  // Аудитория меняется — прежняя оценка устарела и обманывает.
  useEffect(() => {
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, profileType, verifyState, activeDays, withCompany]);

  function validate(): boolean {
    const bad: Record<string, string> = {};
    if (channels.length === 0) bad.channels = 'Выберите хотя бы один канал';
    if (!body.trim()) bad.body = 'Напишите текст сообщения';
    if (Object.keys(bad).length) return form.fail(bad);

    form.reset();
    return true;
  }

  async function count() {
    if (!validate()) return;

    setBusy(true);
    try {
      setPreview(
        await api.admin.broadcast({
          channels,
          subject,
          body,
          audience,
          target,
          chatId: chatId.trim() || undefined,
          dryRun: true,
        }),
      );
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!validate()) return;
    if (!preview) return void count();

    if (
      !(await confirm(
        target === 'CHAT'
          ? 'Отправить сообщение в группу? Убрать его можно будет в течение двух суток.'
          : `Отправить ${preview.planned} получателям? Убрать сообщение можно ` +
            'будет в течение двух суток.',
      ))
    ) {
      return;
    }

    setBusy(true);
    try {
      const r = await api.admin.broadcast({
        channels,
        subject,
        body,
        audience,
        target,
        chatId: chatId.trim() || undefined,
        dryRun: false,
      });
      await alert(`Отправлено: ${r.sent}. Не доставлено: ${r.failed}.`);
      setBody('');
      setSubject('');
      setPreview(null);
      await loadHistory();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Проверка чата до отправки: есть ли бот там, где его ждут. */
  async function checkChat() {
    setBusy(true);
    try {
      const r = await api.admin.testChat(chatId.trim());
      setChatCheck(
        r.ok
          ? { ok: true, text: `Есть доступ: ${r.title} (${r.type})` }
          : { ok: false, text: `Нет доступа: ${r.error}` },
      );
    } catch (e) {
      setChatCheck({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Убрать отправленное у всех.
   *
   * Спрашиваем подтверждение: действие необратимо в другую сторону —
   * вернуть удалённое сообщение нельзя, придётся слать заново.
   */
  async function recall(b: BroadcastRow) {
    if (!(await confirm(`Убрать это сообщение у ${b.live} получателей?`))) return;

    setBusy(true);
    try {
      const r = await api.admin.recallBroadcast(b.id);
      await alert(
        r.failed > 0
          ? `Убрано у ${r.done}. У ${r.failed} не вышло: сообщение старше двух ` +
            'суток, удалено получателем или бот больше не в чате.'
          : `Убрано у ${r.done} получателей.`,
      );
      await loadHistory();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Поправить текст у всех.
   *
   * Часто нужнее отзыва: опечатку в цене или дате исправляют на месте, и
   * получателю не приходит второе сообщение вдогонку.
   */
  async function editSent(b: BroadcastRow) {
    const next = window.prompt('Новый текст сообщения:', b.body)?.trim();
    if (!next) return;

    setBusy(true);
    try {
      const r = await api.admin.editBroadcast(b.id, b.subject ?? undefined, next);
      await alert(
        r.failed > 0
          ? `Поправлено у ${r.done}. У ${r.failed} не вышло — сообщение старше ` +
            'двух суток или удалено получателем.'
          : `Поправлено у ${r.done} получателей.`,
      );
      await loadHistory();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="meta">
        Уходит только тем, кто не отключил соответствующий канал у себя
        в профиле. Это его решение, и обойти его нельзя.
      </p>

      <div className="card">
        {/* Адресат выбирается первым: от него зависит всё остальное —
            и каналы, и сегменты, и то, сколько людей это увидит. */}
        <div className="field">
          <label>Кому отправляем</label>
          <div className="chips">
            {BROADCAST_TARGETS.map((t) => (
              <button
                key={t}
                type="button"
                className={target === t ? 'chip chip--on' : 'chip'}
                onClick={() => {
                  setTarget(t);
                  setPreview(null);
                  setChatCheck(null);
                }}
              >
                {BROADCAST_TARGET_LABEL[t]}
              </button>
            ))}
          </div>
        </div>

        {target === 'CHAT' && (
          <div className="field">
            <label>Группа или канал</label>
            <div className="offer__row">
              <input
                placeholder="@my_channel или -1001234567890"
                value={chatId}
                onChange={(e) => {
                  setChatId(e.target.value);
                  setChatCheck(null);
                  setPreview(null);
                }}
              />
              {/* Проверка до отправки: написать в группу, где бота нет, и
                  узнать об этом из тишины — худший способ настраивать. */}
              <button
                type="button"
                className="secondary"
                disabled={!chatId.trim() || busy}
                onClick={() => void checkChat()}
              >
                Проверить
              </button>
            </div>
            {chatCheck && (
              <div className={chatCheck.ok ? 'meta' : 'error'}>{chatCheck.text}</div>
            )}
            <span className="meta">
              Бот должен состоять в группе, а в канале — быть администратором
              с правом публикации. Идентификатор можно взять из настроек
              интеграций, если он там задан.
            </span>
          </div>
        )}

        {target === 'USERS' && (
          <div className="field">
            <label>Каналы</label>
          <div {...form.group('channels')}>
            <div className="chips">
              {NOTIFY_CHANNELS.map((c) => (
                <button
                  key={c}
                  className={channels.includes(c) ? 'chip chip--on' : 'chip'}
                  data-tip={NOTIFY_CHANNEL_HINT[c]}
                  onClick={() =>
                    setChannels((prev) =>
                      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
                    )
                  }
                >
                  {NOTIFY_CHANNEL_LABEL[c]}
                </button>
              ))}
            </div>
          </div>
          <FieldError form={form} name="channels" />
          </div>
        )}

        <div className="field">
          <label>Тема</label>
          <input
            placeholder="Только для почты"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="field">
          <label>Текст</label>
          <textarea
            {...form.field('body')}
            rows={5}
            placeholder="Коротко и по делу: длинные рассылки не читают"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <FieldError form={form} name="body" />
        </div>
      </div>

      {target === 'USERS' && (
      <div className="card">
        <h3>Сегмент</h3>

        <div className="field-row">
          <div className="field">
            <label>Роль</label>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="">Все</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Профиль</label>
            <select
              value={profileType}
              onChange={(e) => setProfileType(e.target.value as ProfileType)}
            >
              <option value="">Любой</option>
              {PROFILE_TYPES.map((p) => (
                <option key={p} value={p}>
                  {PROFILE_TYPE_LABEL[p]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label>Проверка</label>
            <select
              value={verifyState}
              onChange={(e) => setVerifyState(e.target.value as VerifyState)}
            >
              <option value="">Любая</option>
              {VERIFY_STATES.map((v) => (
                <option key={v} value={v}>
                  {VERIFY_STATE_LABEL[v]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Заходил</label>
            <select
              value={activeDays}
              onChange={(e) =>
                setActiveDays(e.target.value ? Number(e.target.value) : '')
              }
            >
              <option value="">Когда угодно</option>
              <option value="7">За неделю</option>
              <option value="30">За месяц</option>
              <option value="90">За три месяца</option>
            </select>
          </div>
          <div className="field">
            <label>Компания</label>
            <select
              value={withCompany}
              onChange={(e) => setWithCompany(e.target.value as '' | 'yes' | 'no')}
            >
              <option value="">Неважно</option>
              <option value="yes">Только с компанией</option>
              <option value="no">Только без компании</option>
            </select>
          </div>
        </div>

      </div>
      )}

      <div className="card">
        {/* Сначала счёт, потом отправка: единственный способ узнать, что
            письмо уйдёт троим, а не всей бирже. Для группы счёт всё
            равно нужен — он проверяет, что бот туда достучится. */}
        <div className="row" style={{ gap: 'var(--s2)' }}>
          <button className="secondary" disabled={busy} onClick={() => void count()}>
            {target === 'CHAT' ? 'Проверить перед отправкой' : 'Посчитать получателей'}
          </button>
          <button disabled={busy || !preview} onClick={() => void submit()}>
            {preview
              ? target === 'CHAT'
                ? 'Отправить в группу'
                : `Отправить ${preview.planned}`
              : 'Сначала проверьте'}
          </button>
        </div>

        {preview && (
          <div className="meta" style={{ marginTop: 'var(--s3)' }}>
            {target === 'CHAT' ? (
              <>Уйдёт одно сообщение в выбранный чат.</>
            ) : (
              <>
                Подходит получателей: <b>{preview.planned}</b>. Часть может не
                получить сообщение, если отключила канал.
              </>
            )}
            <br />
            Убрать или поправить отправленное можно в течение двух суток — дальше
            Telegram не даёт трогать свои сообщения.
          </div>
        )}
      </div>

      <h3 className="sect">История</h3>

      <p className="meta">
        Сюда же попадают рассылки из бота: команда /broadcast шлёт объявление
        всем участникам с Telegram. Разбор, отзыв и правка у них те же.
      </p>

      {/* Список и разбор, как на остальных вкладках: отправленное читают
          глазами по столбцам — сколько ушло, сколько ещё висит, — а текст
          целиком и кнопки нужны в одной рассылке из десяти. */}
      <AdminList
        rows={history}
        keyOf={(b) => b.id}
        empty="Рассылок ещё не было"
        // Отозванная подсвечена: её ищут, чтобы понять, что именно убрали.
        highlight={(b) => Boolean(b.recalledAt)}
        action={{ label: 'Открыть', onClick: (b) => setExpanded(b.id) }}
        columns={[
          {
            title: 'Рассылка',
            cell: (b) => (
              <>
                <strong>{b.subject || 'Без темы'}</strong>
                {b.viaBot && (
                  <span className="badge" style={{ marginLeft: 'var(--s2)' }}>
                    из бота
                  </span>
                )}
                <span className="meta" style={{ display: 'block' }}>
                  {b.body.slice(0, 120)}
                  {b.body.length > 120 ? '…' : ''}
                </span>
              </>
            ),
          },
          {
            title: 'Каналы',
            width: '150px',
            labelOnPhone: true,
            cell: (b) => b.channels.map((c) => NOTIFY_CHANNEL_LABEL[c]).join(', '),
          },
          {
            title: 'Ушло',
            width: '130px',
            labelOnPhone: true,
            inline: true,
            cell: (b) => (
              <span className="tabular">
                {b.sent} из {b.planned}
              </span>
            ),
          },
          {
            title: 'Ещё висит',
            width: '120px',
            labelOnPhone: true,
            inline: true,
            // Именно это число решает, можно ли ещё убрать: у скольких
            // получателей сообщение сейчас в чате.
            cell: (b) => <span className="tabular">{b.live}</span>,
          },
          {
            title: 'Состояние',
            width: '150px',
            labelOnPhone: true,
            inline: true,
            cell: (b) =>
              b.recalledAt ? (
                <span className="badge badge--warn">отозвана</span>
              ) : b.editedAt ? (
                <span className="badge">правлена</span>
              ) : b.failed > 0 ? (
                <span className="meta">не дошло {b.failed}</span>
              ) : (
                <span className="meta">—</span>
              ),
          },
          {
            title: 'Когда',
            width: '140px',
            labelOnPhone: true,
            inline: true,
            cell: (b) => (
              <span className="tabular">
                {new Date(b.createdAt).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            ),
          },
        ]}
      />

      <AdminSheet
        open={openSent !== null}
        onClose={() => setExpanded(null)}
        title={openSent?.subject || 'Без темы'}
        subtitle={
          openSent && (
            <>
              {openSent.authorName} · {new Date(openSent.createdAt).toLocaleString('ru-RU')}
            </>
          )
        }
        actions={
          openSent && (
            <>
              <button className="secondary" onClick={() => setExpanded(null)}>
                Закрыть
              </button>
              {/* Убрать и поправить можно, пока Telegram позволяет трогать
                  свои сообщения, — примерно двое суток. Дальше кнопок нет:
                  не предлагаем того, что заведомо не сработает. */}
              {openSent.live > 0 && canRecall(openSent.createdAt) && (
                <>
                  <button
                    className="secondary"
                    disabled={busy}
                    onClick={() => void editSent(openSent)}
                  >
                    Поправить текст
                  </button>
                  <button
                    className="danger"
                    disabled={busy}
                    onClick={() => void recall(openSent)}
                  >
                    Убрать у всех
                  </button>
                </>
              )}
            </>
          )
        }
      >
        {openSent && (
          <>
            <div className="row">
              <span className="meta">Каналы</span>
              <span>{openSent.channels.map((c) => NOTIFY_CHANNEL_LABEL[c]).join(', ')}</span>
            </div>
            <div className="row">
              <span className="meta">Отобрано</span>
              <span className="tabular">{openSent.planned}</span>
            </div>
            <div className="row">
              <span className="meta">Отправлено</span>
              <span className="tabular">{openSent.sent}</span>
            </div>
            {openSent.failed > 0 && (
              <div className="row">
                <span className="meta">Не дошло</span>
                <span className="tabular">{openSent.failed}</span>
              </div>
            )}
            <div className="row">
              <span className="meta">Сейчас в чатах</span>
              <span className="tabular">{openSent.live}</span>
            </div>
            {openSent.recalledAt && (
              <div className="row">
                <span className="meta">Отозвана</span>
                <span className="tabular">
                  {new Date(openSent.recalledAt).toLocaleString('ru-RU')}
                </span>
              </div>
            )}
            {openSent.editedAt && (
              <div className="row">
                <span className="meta">Правлена</span>
                <span className="tabular">
                  {new Date(openSent.editedAt).toLocaleString('ru-RU')}
                </span>
              </div>
            )}

            <div className="divider" />
            {/* Текст целиком: по обрезанному нельзя решить, отзывать или нет. */}
            <div style={{ whiteSpace: 'pre-wrap' }}>{openSent.body}</div>

            {openSent.live > 0 && !canRecall(openSent.createdAt) && (
              <div className="meta" style={{ marginTop: 'var(--s3)' }}>
                Прошло больше двух суток — Telegram уже не даёт убрать или
                поправить это сообщение.
              </div>
            )}
          </>
        )}
      </AdminSheet>
    </>
  );
}
